// Coronary risk score: five factor percentiles, summed and scaled.
// Spec: NGF-formulas-verified-v2.md § Coronary risk score. Percentile tables are the legacy tblRisk.
import tblRisk from './data/tblRisk.json' with { type: 'json' };
import { round } from './num.ts';
import type { FitnessClass, Sex } from './types.ts';

interface RiskRow { sex: string; ageFrom: number; ageTo: number; func: string; arg: number; result: number }
const ROWS = tblRisk.rows as RiskRow[];

/**
 * Percentile for `value`: walk the rows for sex/function/age band in stored table order (best
 * percentile first) and return the first whose arg ≥ value; past the last row → 1. Walking in stored
 * order — not sorting by arg or by result — reproduces the legacy program exactly, including a smoking
 * arg of 0 scoring 99 and two typos in tblRisk that are kept as data: male DBP 30–39 lists 67 → 99
 * before 66 → 95, and male DBP 50–59 has 102 → 102 where 102 → 15 was meant.
 * The age band is chosen by age rounded to whole years (39.6 → the 40–49 table).
 */
export function tableLookup(func: 'SBP' | 'DBP' | 'SMOKING', sex: Sex, age: number, value: number): number {
  const a = round(age);
  const rows = ROWS.filter((r) => r.func === func && r.sex === sex && a >= r.ageFrom && a <= r.ageTo);
  return rows.find((r) => r.arg >= value)?.result ?? 1;
}

export const totalCholPercentile = (tc: number): number => (tc < 200 ? 75 : tc < 240 ? 50 : 25);
export const hdlPercentile = (hdl: number): number => (hdl > 60 ? 75 : hdl > 40 ? 50 : 25);

/** Smoking history as recorded on page 1. A never-smoker is all zeros. */
export interface Smoking {
  cigsPerDay: number;
  yearsSmoked: number;
  /** Years since quitting; 0 = still smoking. */
  yearsQuit: number;
}

/** Exposure discount by years since quitting (tiers fitted on 2017+ archive rows). */
function quitFactor(yearsQuit: number): number {
  if (yearsQuit <= 0) return 1;
  if (yearsQuit <= 1) return 0.25;
  if (yearsQuit < 5) return 0.125;
  if (yearsQuit < 10) return 0.075;
  if (yearsQuit < 20) return 0.05;
  return 0.025;
}

/** The legacy "current smoker" checkbox does not enter the score; only these three numbers do. */
export function smokingPercentile(sex: Sex, age: number, s: Smoking): number {
  const arg = 2 * s.cigsPerDay * s.yearsSmoked * quitFactor(s.yearsQuit);
  return tableLookup('SMOKING', sex, age, arg);
}

export function riskClass(score: number): FitnessClass {
  if (score <= 26) return 'Poor';
  if (score <= 53) return 'Fair';
  if (score <= 79) return 'Average';
  if (score <= 105) return 'Good';
  return 'Excellent';
}

export interface CoronaryRiskInput {
  sex: Sex;
  age: number;
  sbp: number;
  dbp: number;
  totalChol: number;
  hdl: number;
  smoking: Smoking;
}

export interface CoronaryRiskResult {
  percentiles: { sbp: number; dbp: number; totalChol: number; hdl: number; smoking: number };
  score: number;
  riskClass: FitnessClass;
}

export function scoreCoronaryRisk(x: CoronaryRiskInput): CoronaryRiskResult {
  const p = {
    sbp: tableLookup('SBP', x.sex, x.age, x.sbp),
    dbp: tableLookup('DBP', x.sex, x.age, x.dbp),
    totalChol: totalCholPercentile(x.totalChol),
    hdl: hdlPercentile(x.hdl),
    smoking: smokingPercentile(x.sex, x.age, x.smoking),
  };
  const score = round(0.26868 * (p.sbp + p.dbp + p.totalChol + p.hdl + p.smoking));
  return { percentiles: p, score, riskClass: riskClass(score) };
}
